import { FC, useState, useEffect, useMemo, Fragment, useCallback } from "react";
import { toasts } from "@/shared/utils/toasts";
import { useParams } from "react-router-dom";
import { Formik, FormikHelpers } from "formik";
import { t } from "i18next";
import { DateTime } from "luxon";

// API
import {
  useMarketingTagsQuery,
  useCreateMarketingTagMutation,
  useUpdateMarketingTagMutation,
  useDeleteMarketingTagMutation,
} from "@/app/marketing-tags/marketing-tags.api";

import {
  MarketingTagValues,
  tagsMarketingInitialValues,
  tagsMarketingValidationSchema,
} from "@/shared/schemas/settings.schema";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { Page } from "@/shared/components/atoms/Page";
import {
  DataTable,
  DataTableAction,
  DataTableColumn,
} from "@/shared/components/organisms/DataTable";
import { MarketingTag } from "@/app/marketing-tags";
import { faPencil, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { AcceptOrCancelModal } from "@/shared/components/organisms/AcceptOrCancelModal";
import { current } from "@reduxjs/toolkit";
import { PlusIcon } from "lucide-react";

export const MarketingTags: FC<any> = () => {
  const { id: branchId } = useParams();
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [currentEditMarketingTag, setCurrentEditMarketingTag] = useState<
    MarketingTag | undefined
  >();
  const [currentDeleteMarketingTag, setCurrentDeleteMarketingTag] = useState<
    MarketingTag | undefined
  >();

  const marketingTagsQuery = useMarketingTagsQuery({ branchId, type: 1 });
  const [createMarketingTag, createMarketingTagResult] =
    useCreateMarketingTagMutation();
  const [updateMarketingTag, updateMarketingTagResult] =
    useUpdateMarketingTagMutation();
  const [deleteMarketingTag, deleteMarketingTagResult] =
    useDeleteMarketingTagMutation();

  useEffect(() => {
    if (createMarketingTagResult.isSuccess) {
      toasts.success(
        t("contexts.marketing_tags.create_marketing_tag_success"),
        t("contexts.marketing_tags.create_marketing_tag_success_description")
      );

      setCreateDialogVisible(false);
      createMarketingTagResult.reset();
    }

    if (createMarketingTagResult.isError) {
      toasts.danger(
        t("contexts.marketing_tags.create_marketing_tag_failure"),
        t("contexts.marketing_tags.create_marketing_tag_failure_description")
      );
    }
  }, [createMarketingTagResult]);

  useEffect(() => {
    if (updateMarketingTagResult.isSuccess) {
      toasts.success(
        t("contexts.marketing_tags.update_marketing_tag_success"),
        t("contexts.marketing_tags.update_marketing_tag_success_description")
      );

      updateMarketingTagResult.reset();
      setCurrentEditMarketingTag(undefined);
      setCreateDialogVisible(false);
    }

    if (updateMarketingTagResult.isError) {
      toasts.danger(
        t("contexts.marketing_tags.update_marketing_tag_failure"),
        t("contexts.marketing_tags.update_marketing_tag_failure_description")
      );
    }
  }, [updateMarketingTagResult]);

  useEffect(() => {
    if (deleteMarketingTagResult.isSuccess) {
      toasts.success(
        t("contexts.marketing_tags.delete_marketing_tag_success"),
        t("contexts.marketing_tags.delete_marketing_tag_success_description")
      );

      deleteMarketingTagResult.reset();
      setCurrentDeleteMarketingTag(undefined);
    }

    if (deleteMarketingTagResult.isError) {
      toasts.danger(
        t("contexts.marketing_tags.delete_marketing_tag_failure"),
        t("contexts.marketing_tags.delete_marketing_tag_failure_description")
      );
    }
  }, [deleteMarketingTagResult]);

  const handleCreate = () => {
    setCreateDialogVisible(!createDialogVisible);
  };

  const handleSubmit = useCallback(
    (
      values: MarketingTagValues,
      helpers: FormikHelpers<MarketingTagValues>
    ) => {
      console.debug({ branchId, ...values });
      if (currentEditMarketingTag) {
        updateMarketingTag({
          ...values,
          branchId,
          id: currentEditMarketingTag.id,
        });
      } else {
        createMarketingTag({
          ...values,
          branchId,
        });
      }
      helpers.resetForm();
    },
    [currentEditMarketingTag, branchId]
  );

  const handleEditMarketingTag = useCallback((marketingTag: MarketingTag) => {
    setCreateDialogVisible(true);
    setCurrentEditMarketingTag(marketingTag);
  }, []);

  const handleDeleteMarketingTag = useCallback((marketingTag: MarketingTag) => {
    setCurrentDeleteMarketingTag(marketingTag);
  }, []);

  const handleAcceptDeleteMarketingTag = useCallback(() => {
    deleteMarketingTag({
      id: currentDeleteMarketingTag?.id,
      branchId,
    });
  }, [currentDeleteMarketingTag, branchId]);

  const handleCancelDeleteMarketingTag = useCallback(() => {
    setCurrentDeleteMarketingTag(undefined);
  }, []);

  const columns = useMemo<DataTableColumn<MarketingTag>[]>(() => {
    return [
      {
        field: "name",
        label: "Nombre",
      },
      {
        field: "createdAt",
        label: "Fecha de creación",
        mapper: (value) =>
          DateTime.fromISO(value as string).toLocaleString(DateTime.DATE_MED),
      },
    ];
  }, []);

  const actions = useMemo<DataTableAction<MarketingTag>[]>(() => {
    return [
      {
        label: t("contexts.shared.edit"),
        handler: handleEditMarketingTag,
        state: "primary",
      },
      {
        icon: faTrashCan,
        handler: handleDeleteMarketingTag,
        state: "danger",
      },
    ];
  }, []);

  return (
    <Fragment>
      <Page
        title={t("brand.views.marketing_tags")}
        description={t("brand.views.create_marketing_tag")}
      >
        <div className="flex items-center justify-end">
          <button
            onClick={handleCreate}
            type="button"
            className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
          >
            <PlusIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-gray-300 my-4"></div>
        <DataTable<MarketingTag>
          actions={actions}
          columns={columns}
          data={marketingTagsQuery.data ?? []}
          isLoading={
            marketingTagsQuery.isLoading || marketingTagsQuery.isFetching
          }
          onEmptyClick={handleCreate}
          placeholder={t("contexts.marketing_tags.no_marketing_tags")}
        />
      </Page>
      <AcceptOrCancelModal
        title={t("contexts.shared.delete")}
        isLoading={deleteMarketingTagResult.isLoading}
        visible={!!currentDeleteMarketingTag}
        cancelText={t("contexts.shared.cancel")}
        acceptText={t("general.accept")}
        onAccept={handleAcceptDeleteMarketingTag}
        onCancel={handleCancelDeleteMarketingTag}
      >
        Estas seguro que quieres eliminar la etiqueta
      </AcceptOrCancelModal>
      <Formik
        enableReinitialize
        initialValues={currentEditMarketingTag ?? tagsMarketingInitialValues}
        validationSchema={tagsMarketingValidationSchema}
        onSubmit={handleSubmit}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          values,
          resetForm,
        }) => (
          <DialogCreate
            open={createDialogVisible}
            setOpen={handleCreate}
            tittle={t("brand.views.create_marketing_tag")}
            OnclickCreate={handleSubmit}
            OnclickCancel={() => {
              resetForm();
              handleCreate();
            }}
            tittleButtonCreate={t("general.create")}
            tittleButtonCancel={t("general.cancel")}
            isLoading={
              createMarketingTagResult.isLoading ||
              updateMarketingTagResult.isLoading
            }
          >
            <div className="mt-6 mx-2">
              <div className="space-y-12">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        {t("brand.views.caracteristics_tag")}
                      </h2>
                    </div>
                  </div>
                  <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                    <div className="col-span-full">
                      <InputWithCornerHint
                        label={t("brand.views.tag_name")}
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name="name"
                        placeholder={t("brand.views.tag_name")}
                        error={!!errors.name}
                        textError={errors.name}
                        value={values.name}
                        autoCapitalize="none"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        inputMode="text"
                      />
                    </div>
                    <div className="col-span-full">
                      <InputWithCornerHint
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name="color"
                        label={t("brand.views.color")}
                        type="color"
                        error={!!errors.color}
                        textError={errors.color}
                        value={values.color}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogCreate>
        )}
      </Formik>
    </Fragment>
  );

  /* 
  return (
    <div className="flex flex-col items-center h-full w-full mt-2">
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">

        <TableListTagsMarketing
          title={t("brand.views.marketing_tags")}
          OnclickTop={functionOpenDialogCreate}
          isLoading={marketingTagsQuery.isLoading}
          tagsMarketing={marketingTagsQuery.data ?? []}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          currentPage={page}
        />
        {marketingTagsQuery.data && (
          <PaginationUI
            currentPage={page}
            totalPages={Math.ceil((marketingTagsQuery?.data.length ?? 0) / pageSize)}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            totalResults={marketingTagsQuery?.data.length ?? 0}
          />
        )}

        <Formik
          enableReinitialize
          initialValues={currentBillEdit ?? tagsMarketingInitialValues}
          validationSchema={tagsMarketingValidationSchema}
          onSubmit={(values, formikHelpers) => {
            
          }}
        >
          {({
            handleChange,
            handleSubmit,
            errors,
            values,
            setFieldValue,
            resetForm,
          }) => (
            <DialogCreate
              open={openDialogCreate}
              setOpen={functionOpenDialogCreate}
              tittle={t("brand.views.create_marketing_tag")}
              OnclickCreate={() => {
                if (currentBillEdit) {
                  updateMarketingTag({
                    ...values,
                    branchId: id,
                    id: currentBillEdit.id,
                  });
                } else {
                  createMarketingTag({
                    ...values,
                    branchId: id,
                  });
                }
                resetForm();
                setBillFormVisible(false);
                setCurrentBillEdit(undefined);
                functionOpenDialogCreate();
              }}
              OnclickCancel={() => {
                resetForm();
                setBillFormVisible(false);
                functionOpenDialogCreate();
              }}
              tittleButtonCreate={t("general.create")}
              tittleButtonCancel={t("general.cancel")}
            >
              <form onSubmit={handleSubmit}>
                <div className="mt-6 mx-2">
                  <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("brand.views.caracteristics_tag")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("brand.views.tag_name")}
                            type="text"
                            onChange={handleChange}
                            name="name"
                            placeholder={t("brand.views.tag_name")}
                            error={!!errors.name}
                            textError={errors.name}
                            value={values.name}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            onChange={(e) => {
                              setFieldValue("color", e.target.value);
                            }}
                            name="color"
                            label={t("brand.views.color")}
                            type="color"
                            error={!!errors.color}
                            textError={errors.color}
                            value={values.color}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </DialogCreate>
          )}
        </Formik>

      </div>
    </div>
  ); */
};
